import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import BulkInquiryModal from '../components/BulkInquiryModal';
import { acceptQuoteByToken } from '../api/inquiry.api';

// ── Quote Acceptance sub-view ──────────────────────────────────────────────
function AcceptQuoteView() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const inquiryNumber = searchParams.get('inquiry');

    const [state, setState] = useState('loading'); // loading | success | error
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!token || !inquiryNumber) {
            setState('error');
            setMessage('Invalid quote acceptance link.');
            return;
        }

        acceptQuoteByToken({ token, inquiryNumber })
            .then((res) => {
                if (res.data?.isOk) {
                    setState('success');
                    setMessage(res.data.message || 'Quote accepted successfully.');
                } else {
                    setState('error');
                    setMessage(res.data?.message || 'Failed to accept quote.');
                }
            })
            .catch(() => {
                setState('error');
                setMessage('Something went wrong. Please contact support.');
            });
    }, [token, inquiryNumber]);

    const icon = { loading: '⏳', success: '✅', error: '❌' }[state];
    const color = { loading: 'text-gray-600', success: 'text-green-700', error: 'text-red-600' }[state];

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
            <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
                <div className="text-5xl mb-5">{icon}</div>
                <h1 className={`text-xl font-bold mb-3 ${color}`}>
                    {state === 'loading' && 'Processing your acceptance…'}
                    {state === 'success' && 'Quote Accepted!'}
                    {state === 'error' && 'Something Went Wrong'}
                </h1>
                <p className="text-gray-600 text-sm">{message}</p>
                {state !== 'loading' && (
                    <a href="/" className="mt-6 inline-block px-6 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition">
                        Back to Home
                    </a>
                )}
            </div>
        </div>
    );
}

// ── Main landing page ──────────────────────────────────────────────────────
export default function BulkOrderPage() {
    const [searchParams] = useSearchParams();
    const isAcceptFlow = searchParams.get('token') && searchParams.get('inquiry');

    const [modalOpen, setModalOpen] = useState(false);

    if (isAcceptFlow) return <AcceptQuoteView />;

    return (
        <div className="min-h-screen bg-white">
            {/* Hero */}
            <section className="bg-gray-900 text-white py-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <span className="inline-block bg-white/10 text-white text-sm font-medium px-4 py-1.5 rounded-full mb-5">
                        Corporate &amp; Bulk Orders
                    </span>
                    <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-5">
                        Buy More,<br />Save More
                    </h1>
                    <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
                        Get exclusive bulk pricing, dedicated account management, and priority delivery for your business or organisation.
                    </p>
                    <button
                        onClick={() => setModalOpen(true)}
                        className="px-8 py-3.5 bg-white text-gray-900 rounded-xl font-bold text-base hover:bg-gray-100 transition"
                    >
                        Request a Quote
                    </button>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-16 px-4 bg-gray-50">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">Why Buy in Bulk with Us?</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { icon: '💰', title: 'Competitive Pricing', desc: 'Volume discounts negotiated to maximise your budget.' },
                            { icon: '📦', title: 'Priority Fulfilment', desc: 'Dedicated stock allocation so your order ships first.' },
                            { icon: '🤝', title: 'Dedicated Account Manager', desc: 'A single point of contact from quote to delivery.' },
                            { icon: '📄', title: 'Formal Quotations', desc: 'Professional PDF quotes sent directly to your inbox.' },
                            { icon: '🚚', title: 'Flexible Delivery', desc: 'Custom delivery schedules to suit your project timeline.' },
                            { icon: '🏷️', title: 'Custom Branding', desc: 'Request custom packaging or branding for your order.' },
                        ].map(({ icon, title, desc }) => (
                            <div key={title} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                                <div className="text-3xl mb-3">{icon}</div>
                                <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
                                <p className="text-gray-500 text-sm">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="py-16 px-4">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">How It Works</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 relative">
                        {[
                            { step: '1', label: 'Submit Inquiry', desc: 'Fill in the form with your product requirements and contact details.' },
                            { step: '2', label: 'We Review', desc: 'Our sales team reviews your request within 1–2 business days.' },
                            { step: '3', label: 'Receive Quote', desc: 'A personalised PDF quote is emailed directly to you.' },
                            { step: '4', label: 'Confirm & Pay', desc: 'Accept the quote and complete payment to finalise your order.' },
                        ].map(({ step, label, desc }) => (
                            <div key={step} className="text-center">
                                <div className="w-12 h-12 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold text-lg mx-auto mb-3">
                                    {step}
                                </div>
                                <h3 className="font-semibold text-gray-900 mb-1">{label}</h3>
                                <p className="text-gray-500 text-sm">{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-gray-900 text-white py-16 px-4 text-center">
                <h2 className="text-2xl font-bold mb-4">Ready to Place a Bulk Order?</h2>
                <p className="text-gray-300 mb-8 max-w-xl mx-auto">
                    Tell us what you need and we'll build a competitive quote tailored to your business.
                </p>
                <button
                    onClick={() => setModalOpen(true)}
                    className="px-8 py-3.5 bg-white text-gray-900 rounded-xl font-bold text-base hover:bg-gray-100 transition"
                >
                    Get a Free Quote
                </button>
            </section>

            <BulkInquiryModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                source="landing_page"
            />
        </div>
    );
}
